import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Waves, ExternalLink, Bot, TrendingUp, ArrowDownToLine, ArrowUpFromLine,
  ArrowLeftRight, Layers, RefreshCw, Compass, AlertTriangle
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  mega: { label: 'MEGA WHALE', cls: 'text-red-400 bg-red-400/10 border-red-400/30' },
  whale: { label: 'WHALE', cls: 'text-[#C5A059] bg-[#C5A059]/10 border-[#C5A059]/30' },
  large: { label: 'LARGE', cls: 'text-blue-400 bg-blue-400/10 border-blue-400/30' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ago`;
};

const fmtUsd = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : `$${(v / 1e3).toFixed(0)}K`;

const MEANING_CARDS = [
  {
    icon: ArrowDownToLine, title: 'Exchange Inflows',
    desc: 'Large amounts moving TO exchanges often signal intent to sell — coins must sit on an exchange before they can hit the order book. Repeated big inflows can precede sell pressure.',
  },
  {
    icon: ArrowUpFromLine, title: 'Exchange Outflows',
    desc: 'Coins leaving exchanges into private wallets usually mean long-term holding (accumulation or cold storage). Sustained outflows shrink sellable supply — historically bullish.',
  },
  {
    icon: ArrowLeftRight, title: 'Wallet-to-Wallet',
    desc: 'Whale-to-whale or custody transfers often reflect OTC deals or internal shuffling — big headline numbers that may have zero market impact. Context matters more than size.',
  },
  {
    icon: Layers, title: 'Why Size Moves Price',
    desc: 'Order books are thin: a $50M market sell can eat multiple price levels instantly. Big moves also SIGNAL — other traders front-run what they think whales are about to do.',
  },
];

export default function WhaleWatch() {
  const [feed, setFeed] = useState(null);
  const [insight, setInsight] = useState(null);
  const [narratives, setNarratives] = useState(null);
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    axios.get(`${API}/whale/feed`).then((r) => setFeed(r.data)).catch(() => {});
    axios.get(`${API}/whale/insight`).then((r) => setInsight(r.data)).catch(() => {});
    axios.get(`${API}/whale/narratives`).then((r) => setNarratives(r.data)).catch(() => {});
  }, []);

  const txs = (feed?.transactions || []).filter((t) => filter === 'ALL' || t.asset === filter);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Hero */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-2 mb-4">
              <Waves className="text-[#C5A059]" size={16} />
              <span className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider">Live On-Chain Data</span>
            </div>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-3" data-testid="whale-watch-title">
              Whale <span className="text-[#C5A059]">Watch</span>
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl">
              The largest Bitcoin and Ethereum transactions hitting the blockchain right now — and what smart money movement actually means for price.
            </p>
          </div>

          {/* AI Insight */}
          {insight && (
            <div className="bg-gradient-to-br from-[#17130B] to-[#0B0A08] border border-[#C5A059]/30 rounded-xl p-6 mb-8" data-testid="whale-insight">
              <div className="flex items-center justify-between mb-3">
                <h2 className="flex items-center gap-2 text-[#C5A059] font-bold text-sm uppercase tracking-wider">
                  <Bot size={15} /> AI Flow Analysis
                </h2>
                {insight.activity_level && (
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                    insight.activity_level === 'extreme' ? 'text-red-400 border-red-400/40 bg-red-400/10'
                    : insight.activity_level === 'elevated' ? 'text-[#C5A059] border-[#C5A059]/40 bg-[#C5A059]/10'
                    : 'text-gray-400 border-gray-600 bg-black/30'}`}>
                    {insight.activity_level} activity
                  </span>
                )}
              </div>
              <p className="font-manrope font-bold text-xl text-white mb-2">{insight.headline}</p>
              <p className="text-gray-300 text-sm leading-relaxed mb-2">{insight.analysis}</p>
              <p className="text-gray-400 text-sm leading-relaxed"><span className="text-[#C5A059] font-semibold">Price impact: </span>{insight.price_impact}</p>
            </div>
          )}

          {/* Transactions table */}
          <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden mb-10">
            <div className="px-5 py-4 border-b border-white/10 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-manrope font-bold text-lg text-white">Largest Recent Transactions</h2>
              <div className="flex items-center gap-2">
                {['ALL', 'BTC', 'ETH'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    data-testid={`whale-filter-${f.toLowerCase()}`}
                    className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${filter === f ? 'bg-[#C5A059] text-black' : 'bg-white/5 text-gray-400 hover:text-white'}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            {!feed ? (
              <div className="flex items-center justify-center py-14"><RefreshCw className="animate-spin text-[#C5A059]" size={22} /></div>
            ) : txs.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-10">No large transactions detected in the latest blocks — whales are quiet right now.</p>
            ) : (
              <div className="divide-y divide-white/5" data-testid="whale-tx-list">
                {txs.map((t, i) => {
                  const tier = TIER_STYLES[t.tier] || TIER_STYLES.large;
                  return (
                    <div key={t.hash + i} className="px-5 py-3.5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors">
                      <span className={`shrink-0 w-14 text-center font-mono font-bold text-sm ${t.asset === 'BTC' ? 'text-orange-400' : 'text-indigo-400'}`}>{t.asset}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm">{t.amount.toLocaleString()} {t.asset} <span className="text-gray-500 font-normal">· {fmtUsd(t.amount_usd)}</span></p>
                        <p className="text-gray-600 text-xs font-mono truncate">{t.hash.slice(0, 22)}…</p>
                      </div>
                      <span className={`hidden sm:inline-flex shrink-0 px-2.5 py-1 rounded-full border text-[10px] font-bold ${tier.cls}`}>{tier.label}</span>
                      <span className="shrink-0 text-gray-500 text-xs w-20 text-right">{timeAgo(t.time)}</span>
                      <a href={t.explorer} target="_blank" rel="noopener noreferrer" className="shrink-0 text-gray-500 hover:text-[#C5A059] transition-colors" data-testid={`whale-explorer-${i}`}>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  );
                })}
              </div>
            )}
            {feed && (
              <p className="px-5 py-3 text-gray-600 text-xs border-t border-white/5">
                Scanned live blocks · BTC ≥ $1M, ETH ≥ $250K native transfers · Updated {timeAgo(feed.updated_at)} · Refreshes every 10 min
              </p>
            )}
          </div>

          {/* What it means */}
          <div className="mb-12">
            <h2 className="font-manrope font-bold text-2xl text-white mb-2">What Do Whale Moves <span className="text-[#C5A059]">Mean?</span></h2>
            <p className="text-gray-400 text-sm mb-6 max-w-3xl">
              A single transaction is never a signal by itself — but flows in aggregate tell you where conviction is building. Here's how professionals read them:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="whale-meaning-cards">
              {MEANING_CARDS.map((c) => (
                <div key={c.title} className="bg-[#111111] border border-white/10 rounded-lg p-5 hover:border-[#C5A059]/40 transition-colors">
                  <div className="w-10 h-10 rounded-sm bg-[#C5A059]/10 border border-[#C5A059]/30 flex items-center justify-center mb-3">
                    <c.icon className="text-[#C5A059]" size={17} />
                  </div>
                  <h3 className="text-white font-semibold text-sm mb-1.5">{c.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-white/[0.02] border border-white/10 rounded-lg px-4 py-3">
              <AlertTriangle size={14} className="text-[#C5A059] shrink-0 mt-0.5" />
              <p>Without address labels, a raw transaction can't tell you if coins went TO or FROM an exchange. Treat single transactions as awareness, not signals — combine with technicals and sentiment before acting.</p>
            </div>
          </div>

          {/* Narrative investing */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Compass className="text-[#C5A059]" size={20} />
              <h2 className="font-manrope font-bold text-2xl text-white">Why Invest in a <span className="text-[#C5A059]">Narrative?</span></h2>
            </div>
            <p className="text-gray-400 text-sm mb-6 max-w-3xl">
              Crypto capital rotates in stories: money chases attention, attention chases catalysts, and prices follow flows — often before fundamentals catch up.
              Owning the strongest narrative early is how cycles are won. These are the narratives attracting capital right now, refreshed twice daily by AI from live market intelligence:
            </p>
            {!narratives ? (
              <div className="flex items-center justify-center py-10"><RefreshCw className="animate-spin text-[#C5A059]" size={20} /></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="narrative-cards">
                {(narratives.narratives || []).map((n) => (
                  <div key={n.name} className="bg-[#111111] border border-white/10 rounded-xl p-6 hover:border-[#C5A059]/40 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-manrope font-bold text-lg text-white">{n.name}</h3>
                      <div className="flex items-center gap-1.5">
                        <TrendingUp size={13} className="text-[#C5A059]" />
                        <span className="text-[#C5A059] font-bold text-sm">{n.strength}/10</span>
                      </div>
                    </div>
                    <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-4">
                      <div className="h-full bg-[#C5A059] rounded-full" style={{ width: `${(n.strength || 0) * 10}%` }} />
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-2">{n.thesis}</p>
                    <p className="text-gray-400 text-xs mb-3"><span className="text-[#C5A059] font-semibold">Why now: </span>{n.why_now}</p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(n.key_assets || []).map((a) => (
                        <span key={a} className="px-2 py-0.5 rounded-full bg-[#C5A059]/10 border border-[#C5A059]/25 text-[#C5A059] text-[10px] font-bold">{a}</span>
                      ))}
                    </div>
                    <p className="text-gray-500 text-xs"><span className="text-red-400/80 font-semibold">Risk: </span>{n.risks}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-gray-600 text-xs mt-4">Educational content, not financial advice. Narratives can reverse fast — always size positions responsibly.</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
